// Single-camera player stage.
//
// Mounts the two `<video>` elements that the stitch engine flips between.
// Imperative drive happens in `useStitchPlayer`; this component just
// provides the DOM nodes (via `attach`) and renders the OSD chrome.

import { useEffect, useRef } from "react";

import type { ClipState } from "../hooks/useStitchPlayer";

export interface PlayerStageProps {
  clip: ClipState | null;
  loading: boolean;
  /** Imperative attach: pass both <video>s up to the engine. */
  attach(videoA: HTMLVideoElement | null, videoB: HTMLVideoElement | null): void;
  /** OSD timestamp (wall-clock ms; computed by container from clip.virtualMs). */
  osdWallMs: number | null;
}

export function PlayerStage({
  clip,
  loading,
  attach,
  osdWallMs,
}: PlayerStageProps): JSX.Element {
  const aRef = useRef<HTMLVideoElement | null>(null);
  const bRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    attach(aRef.current, bRef.current);
    return () => attach(null, null);
  }, [attach]);

  return (
    <div className="rec-stage" data-loaded={clip ? "true" : "false"}>
      <video ref={aRef} playsInline muted preload="auto" />
      <video ref={bRef} playsInline muted preload="auto" />
      {clip && (
        <>
          <div className="rec-osd">
            <span className="rec-osd-cam">{clip.meta.name}</span>
            {osdWallMs !== null && (
              <span className="rec-osd-ts">{new Date(osdWallMs).toLocaleString()}</span>
            )}
          </div>
          {clip.playing && (
            <div className="rec-rec-dot">
              <span className="rec-dot-b" aria-hidden="true" />
              PLAYING
            </div>
          )}
          <div className={`rec-qbadge${clip.hdDisabled ? " capped" : ""}`}>
            <b>{clip.tier === "hd" ? "HD · 1080p" : "SD · 360p"}</b>
            {clip.hdDisabled ? " · HD capped" : clip.tier === "hd" ? " · 25 fps" : " · 5 fps"}
          </div>
        </>
      )}
      {loading && (
        <div className="rec-stage-spinner" role="status" aria-label="Loading footage">
          <div className="rec-ring" />
        </div>
      )}
      {!clip && !loading && (
        <div className="rec-stage-empty">
          <div>
            <div className="rec-big">No clip loaded</div>
            <div>
              Click an event on the timeline, or drag across a lane to select any
              time range — even with no event.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
